import { useEffect, useState } from "react";
import { CheckCircle2, Clock, CreditCard, ExternalLink, LogOut, Palette, Store, AlertTriangle, Eye, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const TYPE_OPTS = ["ecommerce", "restaurant", "services", "portfolio", "business", "other"];

function StepPill({ n, label, done, active }) {
  return (
    <div data-testid={`step-pill-${n}`} className={`flex items-center gap-2.5 px-4 py-2 rounded-full border text-sm font-bold ${done ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : active ? "border-primary/40 bg-primary/10" : "border-border text-muted-foreground"}`}>
      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs ${done ? "bg-emerald-500 text-white" : active ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
        {done ? <CheckCircle2 className="h-4 w-4" /> : n}
      </span>
      {label}
    </div>
  );
}

export default function SelfServiceSetup() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [themes, setThemes] = useState([]);
  const [biz, setBiz] = useState({ name: "", type: "ecommerce", phone: "", description: "" });
  const [pay, setPay] = useState({ reference: "", paid_at: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [cat, setCat] = useState("all");

  const load = () =>
    api.get("/self-service/my-request").then((r) => {
      setData(r.data);
      const b = r.data.request?.business || {};
      setBiz({ name: b.name || "", type: b.type || "ecommerce", phone: b.phone || "", description: b.description || "" });
    }).catch((e) => setErr(errMsg(e)));

  useEffect(() => {
    if (!localStorage.getItem("zylo_token")) { window.location.href = "/business-owner/login"; return; }
    load();
    api.get("/public/themes").then((r) => setThemes(r.data.themes || r.data || [])).catch(() => {});
  }, []);

  if (err) {
    return (
      <div data-testid="setup-no-request" className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md w-full text-center space-y-4 rounded-2xl border border-border bg-card p-10">
          <p className="font-display font-bold text-lg">No website request found</p>
          <p className="text-sm text-muted-foreground">{err}</p>
          <a href="/" data-testid="setup-go-home" className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">Go to Home</a>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="h-12 w-12 rounded-xl bg-primary animate-pulse" /></div>;

  const { request, website, payment, settings } = data;
  const approved = request.status === "approved";
  const step1 = Boolean(biz.name);
  const step2 = Boolean(website);
  const categories = ["all", ...new Set(themes.map((t) => t.category).filter(Boolean))];
  const shown = themes.filter((t) => cat === "all" || t.category === cat);

  const saveBiz = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/self-service/business", { business_info: biz });
      toast.success("Business details saved");
      await load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const pickTheme = async (tid) => {
    setBusy(true);
    try {
      await api.post("/self-service/theme", { theme_id: tid });
      toast.success("Theme applied to your website");
      await load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/self-service/payment-submit", pay);
      toast.success("Payment submitted for verification");
      await load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const logout = () => { localStorage.removeItem("zylo_token"); window.location.href = "/"; };

  return (
    <div data-testid="self-service-setup" className="min-h-screen bg-background">
      <header className="border-b border-border/60 sticky top-0 bg-background/80 backdrop-blur z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLogo size="md" />
          <button data-testid="setup-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter">Website Setup</h1>
          <p className="text-muted-foreground mt-2">Complete the steps below — your website goes live right after payment verification.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <StepPill n={1} label="Business Details" done={step1} active={!step1} />
            <StepPill n={2} label="Choose Theme" done={step2} active={step1 && !step2} />
            <StepPill n={3} label="Payment" done={approved} active={step2 && !approved} />
          </div>
        </div>

        {approved && (
          <div data-testid="setup-approved-banner" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 sm:p-8 flex flex-wrap items-center gap-5">
            <div className="h-12 w-12 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center"><PartyPopper className="h-6 w-6" /></div>
            <div className="flex-1 min-w-52">
              <p className="font-display font-bold text-lg">Your website is live!</p>
              <p className="text-sm text-muted-foreground">Payment verified — your 1-year access is active. Manage products, orders and design from your dashboard.</p>
            </div>
            <div className="flex gap-3">
              <Button data-testid="setup-open-dashboard" onClick={() => (window.location.href = "/store")} className="rounded-full font-bold">Open Dashboard</Button>
              {website?.slug && (
                <Button data-testid="setup-visit-site" variant="outline" onClick={() => window.open(`/site/${website.slug}`, "_blank")} className="rounded-full font-bold">
                  <Eye className="h-4 w-4 mr-2" /> Visit Website
                </Button>
              )}
            </div>
          </div>
        )}

        {!approved && payment?.status === "pending" && (
          <div data-testid="setup-pending-banner" className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 sm:p-8 flex items-start gap-5">
            <div className="h-12 w-12 rounded-full bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0"><Clock className="h-6 w-6" /></div>
            <div>
              <p className="font-display font-bold text-lg">Payment under verification</p>
              <p className="text-sm text-muted-foreground mt-1">{settings.verification_message || "Approval is normally completed within 24 hours after payment verification."}</p>
              <p className="text-xs text-muted-foreground mt-2">Submitted {fmtDateTime(payment.created_at)} · Amount {fmtINR(payment.amount)}{payment.reference ? ` · Ref ${payment.reference}` : ""}</p>
            </div>
          </div>
        )}

        {!approved && payment?.status === "needs_info" && (
          <div data-testid="setup-needsinfo-banner" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 sm:p-8 flex items-start gap-5">
            <div className="h-12 w-12 rounded-full bg-red-500/15 text-red-600 flex items-center justify-center shrink-0"><AlertTriangle className="h-6 w-6" /></div>
            <div>
              <p className="font-display font-bold text-lg">More information needed</p>
              <p className="text-sm text-muted-foreground mt-1">{payment.admin_note || "Please resubmit your payment with the correct reference details below."}</p>
            </div>
          </div>
        )}

        {!approved && payment?.status === "rejected" && (
          <div data-testid="setup-rejected-banner" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 sm:p-8 flex items-start gap-5">
            <div className="h-12 w-12 rounded-full bg-red-500/15 text-red-600 flex items-center justify-center shrink-0"><AlertTriangle className="h-6 w-6" /></div>
            <div>
              <p className="font-display font-bold text-lg">Payment could not be verified</p>
              <p className="text-sm text-muted-foreground mt-1">{payment.admin_note || "Please contact support or resubmit with the correct payment reference."}</p>
            </div>
          </div>
        )}

        {/* STEP 1 — Business details */}
        <section data-testid="setup-step-business" className="rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center"><Store className="h-5 w-5" /></div>
            <div>
              <h2 className="font-display font-bold text-xl">1 · Business Details</h2>
              <p className="text-sm text-muted-foreground">This appears on your website.</p>
            </div>
          </div>
          <form onSubmit={saveBiz} className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="overline-label">Business Name</Label>
              <Input data-testid="biz-name" required value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} className="h-12 rounded-md bg-background" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Business Type</Label>
              <select data-testid="biz-type" value={biz.type} onChange={(e) => setBiz({ ...biz, type: e.target.value })} className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm capitalize">
                {TYPE_OPTS.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Phone</Label>
              <Input data-testid="biz-phone" value={biz.phone} onChange={(e) => setBiz({ ...biz, phone: e.target.value })} className="h-12 rounded-md bg-background" placeholder="+91 ..." />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="overline-label">Short Description</Label>
              <textarea data-testid="biz-description" value={biz.description} onChange={(e) => setBiz({ ...biz, description: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-20" placeholder="What does your business do?" />
            </div>
            <div className="sm:col-span-2">
              <Button data-testid="biz-save" type="submit" disabled={busy} className="rounded-full font-bold">Save Details</Button>
            </div>
          </form>
        </section>

        {/* STEP 2 — Theme */}
        <section data-testid="setup-step-theme" className="rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center"><Palette className="h-5 w-5" /></div>
            <div>
              <h2 className="font-display font-bold text-xl">2 · Choose Your Theme</h2>
              <p className="text-sm text-muted-foreground">Preview any design in a new tab — you can change it later.</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap mb-6">
            {categories.map((c) => (
              <button key={c} data-testid={`theme-cat-${c}`} onClick={() => setCat(c)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-colors capitalize ${cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[32rem] overflow-y-auto pr-1">
            {shown.map((t) => {
              const active = request.theme_id === (t.id || t._id);
              const colors = Object.values(t.colors || {}).filter((v) => typeof v === "string" && v.startsWith("#")).slice(0, 4);
              return (
                <div key={t.id || t._id} data-testid={`theme-card-${t.id || t._id}`}
                  className={`rounded-xl border p-4 transition-all ${active ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-foreground/30"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-sm truncate">{t.name}</p>
                    <div className="flex gap-1 shrink-0">
                      {colors.map((c, i) => <span key={i} className="h-4 w-4 rounded-full border border-black/10" style={{ background: c }} />)}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground capitalize mt-0.5">{t.category || "general"}</p>
                  <div className="flex gap-2 mt-3">
                    <button type="button" data-testid={`theme-preview-${t.id || t._id}`}
                      onClick={() => window.open(`/preview/${t.id || t._id}`, "_blank", "noopener")}
                      className="flex-1 h-9 rounded-lg border border-border text-xs font-bold hover:bg-muted transition-colors inline-flex items-center justify-center gap-1.5">
                      <Eye className="h-3.5 w-3.5" /> Preview
                    </button>
                    <button type="button" data-testid={`theme-select-${t.id || t._id}`} disabled={busy || active}
                      onClick={() => pickTheme(t.id || t._id)}
                      className={`flex-1 h-9 rounded-lg text-xs font-bold transition-colors ${active ? "bg-emerald-500/15 text-emerald-600 cursor-default" : "bg-primary text-primary-foreground hover:opacity-90"}`}>
                      {active ? "Selected ✓" : "Select"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* STEP 3 — Payment */}
        {!approved && (
          <section data-testid="setup-step-payment" className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center"><CreditCard className="h-5 w-5" /></div>
              <div>
                <h2 className="font-display font-bold text-xl">3 · Payment</h2>
                <p className="text-sm text-muted-foreground">Pay securely via our official payment page, then submit your reference below.</p>
              </div>
            </div>
            {!website ? (
              <p data-testid="payment-needs-theme" className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-5">Please choose a theme above first — then complete your payment.</p>
            ) : payment?.status === "pending" ? (
              <p className="text-sm text-muted-foreground border border-dashed border-border rounded-xl p-5">Your payment is already submitted. You will be notified as soon as it is verified.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="rounded-xl border border-border bg-muted/40 p-6 space-y-4">
                  <div>
                    <p className="overline-label">Amount payable</p>
                    <p data-testid="payment-amount" className="font-display font-extrabold text-4xl tracking-tight mt-1">{fmtINR(settings.price)}</p>
                    <p className="text-xs text-muted-foreground mt-1">One-time · includes 1 year access</p>
                  </div>
                  <a data-testid="pay-superprofile-link" href={settings.payment_link} target="_blank" rel="noopener noreferrer"
                    className="w-full h-12 rounded-lg bg-primary text-primary-foreground font-bold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
                    Pay {fmtINR(settings.price)} on SuperProfile <ExternalLink className="h-4 w-4" />
                  </a>
                  <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                    <li>Open the payment page and complete {fmtINR(settings.price)} payment.</li>
                    <li>Note down the UTR / transaction reference number.</li>
                    <li>Come back here and submit the reference.</li>
                    <li>We verify and activate your website (usually within 24 hours).</li>
                  </ol>
                </div>
                <form onSubmit={submitPayment} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="overline-label">UTR / Transaction Reference *</Label>
                    <Input data-testid="pay-reference" required value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="h-12 rounded-md bg-background" placeholder="e.g. 427123456789" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="overline-label">Payment Date</Label>
                    <Input data-testid="pay-date" type="date" value={pay.paid_at} onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} className="h-12 rounded-md bg-background" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="overline-label">Note (optional)</Label>
                    <textarea data-testid="pay-note" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })}
                      className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-20" placeholder="Anything that helps us verify faster (payer name, UPI ID used...)" />
                  </div>
                  <Button data-testid="pay-submit" type="submit" disabled={busy} className="w-full h-12 rounded-md font-bold">
                    {busy ? "Submitting..." : "I Have Completed Payment — Submit"}
                  </Button>
                </form>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
